import React, { useState, useEffect } from 'react';
import {
  List,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRedirect,
  useListContext,
  useRefresh,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Avatar,
  Button
} from '@mui/material';
import { 
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';
import { getCustomerTypeColor } from '../../utils/customerTypeColors';
import { CustomerCreateDialog } from '../../components/CustomerCreateDialog';

const ListActions = ({ onCreateClick }: { onCreateClick: () => void }) => (
  <TopToolbar>
    <Button
      onClick={onCreateClick}
      color="primary"
      variant="contained"
      startIcon={<AddIcon />}
    >
      Create
    </Button>
    <ExportButton />
  </TopToolbar>
);

const CustomerCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  // Get primary address
  const getPrimaryAddress = () => {
    if (!record?.locations || record.locations.length === 0) {
      return { line1: '', line2: '' };
    }
    const primaryLocation = record.locations.find((loc: any) => loc.isPrimary) || record.locations[0];
    if (!primaryLocation?.location) {
      return { line1: '', line2: '' };
    }
    const { streetLine1, city, province, postalCode } = primaryLocation.location;
    
    // Line 1: Street address
    const line1 = streetLine1 || '';
    
    // Line 2: City, State/Province, Postal Code
    const line2Parts = [city, province, postalCode].filter(Boolean);
    const line2 = line2Parts.join(', ');
    
    return { line1, line2 };
  };

  // Get primary phone
  const getPrimaryPhone = () => {
    // First check if phone is directly on the record
    if (record?.phone) {
      return record.phone;
    }
    // Then check for phones array
    if (!record?.phones || record.phones.length === 0) {
      return null;
    }
    const primaryPhone = record.phones.find((phone: any) => phone.isPrimary) || record.phones[0];
    return primaryPhone?.phoneNumber;
  };

  // Get primary email
  const getPrimaryEmail = () => {
    // First check if email is directly on the record
    if (record?.email) {
      return record.email;
    }
    // Then check for emails array
    if (!record?.emails || record.emails.length === 0) {
      return null;
    }
    const primaryEmail = record.emails.find((email: any) => email.isPrimary) || record.emails[0];
    return primaryEmail?.emailAddress;
  };

  const address = getPrimaryAddress();
  const phone = getPrimaryPhone();
  const email = getPrimaryEmail();
  const customerTypeName = record?.customerType?.name;
  const customerTypeColor = record?.customerType?.color || getCustomerTypeColor(customerTypeName);
  const zoneName = record?.zone?.name;

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
        borderLeft: `4px solid ${customerTypeColor}`,
        borderRadius: 0,
        minHeight: 'auto',
        mb: 1,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'minmax(300px, 1.5fr) minmax(200px, 1.2fr) minmax(200px, 1fr) 1fr minmax(150px, 0.9fr)',
          },
          gap: { xs: 1, sm: 2 },
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          width: '100%'
        }}>
          {/* Column 1: Avatar and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar 
              sx={{ 
                bgcolor: customerTypeColor,
                width: 36,
                height: 36
              }}
            >
              <BusinessIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="subtitle1" 
                component="h3" 
                sx={{ 
                  fontWeight: 600, 
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {record.displayName}
              </Typography>
            </Box>
          </Box>

          {/* Column 2: Address */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
            <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, width: '100%' }}>
              {address.line1 && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.8rem',
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {address.line1}
                </Typography>
              )}
              {address.line2 && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.75rem',
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {address.line2}
                </Typography>
              )}
              {!address.line1 && !address.line2 && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ fontSize: '0.8rem' }}
                >
                  No address
                </Typography>
              )}
            </Box>
          </Box>

          {/* Column 3: Contact Information */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 0.3 }}>
            {/* Contact Name and Title */}
            {(record?.firstName || record?.lastName || record?.title) && (
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {[record?.firstName, record?.lastName].filter(Boolean).join(' ')}
                </Typography>
                {record?.title && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {record.title}
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Phone and Email */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              {phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {phone}
                  </Typography>
                </Box>
              )}
              {email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {email}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Show dash if no contact info at all */}
            {!record?.firstName && !record?.lastName && !record?.title && !phone && !email && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                -
              </Typography>
            )}
          </Box>

          {/* Column 4: Additional Info (spacer) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}></Box>

          {/* Column 5: Customer Type and Zone */}
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
            {customerTypeName && (
              <Chip 
                label={customerTypeName}
                size="small"
                sx={{ 
                  bgcolor: customerTypeColor,
                  color: 'white',
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.7rem'
                }}
              />
            )}
            {zoneName && (
              <Chip 
                label={zoneName}
                size="small"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.7rem'
                }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CustomerListHeader = () => (
  <Box sx={{
    display: {
      xs: 'none',
      md: 'grid'
    },
    gridTemplateColumns: 'minmax(300px, 1.5fr) minmax(200px, 1.2fr) minmax(200px, 1fr) 1fr minmax(150px, 0.9fr)',
    gap: 2,
    alignItems: 'center',
    px: 2,
    py: 1.5,
    backgroundColor: 'grey.50',
    borderRadius: 0,
    mb: 2,
    border: '1px solid',
    borderColor: 'grey.200'
  }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Customer Name
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Address
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Contact Information
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      {/* Additional Info */}
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
      Type & Zone
    </Typography>
  </Box>
);

const CustomerCardList = () => {
  const { data, isLoading } = useListContext();
  const redirect = useRedirect();

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box>
      <CustomerListHeader />
      {data.map((record: any) => (
        <CustomerCard
          key={record.id}
          record={record}
          onClick={() => redirect(`/customers/${record.id}/show`)}
        />
      ))}
    </Box>
  );
};

export const CustomerList = () => {
  const redirect = useRedirect();
  const refresh = useRefresh();
  const location = useLocation();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Open dialog if coming from create URL
  useEffect(() => {
    if (location.pathname.includes('/create')) {
      setCreateDialogOpen(true);
      // Clean up the URL
      window.history.replaceState({}, '', '/customers');
    }
  }, [location.pathname]);

  const handleCreateCustomer = () => {
    setCreateDialogOpen(true);
  };

  const handleCustomerCreated = (customer: any) => {
    refresh();
    redirect(`/customers/${customer.id}/show`);
  };

  return (
    <Box>
      <PageTitle
        title="Customers"
        titleKey="pages.customers"
        subtitle="Manage your customer database"
        subtitleKey="pages.customers.subtitle"
        icon={<BusinessIcon />}
      />
      <List
        actions={<ListActions onCreateClick={handleCreateCustomer} />}
        perPage={25}
        sort={{ field: 'displayName', order: 'ASC' }}
        empty={<CustomEmptyState resource="customers" onAction={handleCreateCustomer} />}
        sx={{ 
          '& .RaList-main': { mt: 0 },
          '& .RaList-content': { p: 2 }
        }}
      >
        <CustomerCardList />
      </List>
      
      <CustomerCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCustomerCreated}
      />
    </Box>
  );
};
